import React from "react";
import { useApiUrl } from "@refinedev/core";
import { config } from "@src/config";
import { useSmlQueryBuilderContext } from "@src/pages/intelligence-search-v2/context/sml-query-builder-context";
import { useBlockNavigateUnSave } from "@src/hooks/use-block-navigate-unsave";
import { useAuth0Provider } from "@src/libs/auth0-provider";

export type DownloadStatus = { type: "idle" } | { type: "processing" } | { type: "success" };

export const useDownloadCSV = () => {
    const [status, setStatus] = React.useState<DownloadStatus>({ type: "idle" });
    const [error, setError] = React.useState<string | null>(null);
    const apiUrl = useApiUrl("semantic");
    const { getGeneratedJsonQuery, productCode, timeRange } = useSmlQueryBuilderContext();
    const { setWarnWhen } = useBlockNavigateUnSave();
    const { axios } = useAuth0Provider();

    const download = async () => {
        const jsonQuery = getGeneratedJsonQuery();
        if (!jsonQuery) {
            setError("Please run a query first");
            return;
        }
        const { limit: _, ...queryWithoutLimit } = jsonQuery;
        const encodedJsonQuery = btoa(
            JSON.stringify({
                ...queryWithoutLimit,
                limit:1000 * 1000
            }),
        );

        setError(null);
        setWarnWhen(true);

        try {
            // Step 1: Call /export to get job_id
            setStatus({ type: "processing" });
            const sharedHeaders = {
                "product-code": productCode,
                "start-date": timeRange?.[0]?.format(config.DATE_FORMAT),
                "end-date": timeRange?.[1]?.format(config.DATE_FORMAT),
            };
            const exportRes = await axios.get(`${apiUrl}/export`, {
                params: {
                    encoded_json_query: encodedJsonQuery,
                    // compression: "GZIP"
                },
                headers: sharedHeaders,
            });
            const exportData = exportRes.data;
            let downloadUrls: string[] = [];

            if (exportData?.status === "success") {
                // Export already completed, skip polling
                downloadUrls = exportData?.download_urls ?? [];
            } else {
                // Step 2: Poll /download/status/:job_id every 5s
                const jobId: string = exportData?.job_id;
                if (!jobId) throw new Error("No job_id returned from export API");

                while (true) {
                    await new Promise((resolve) => setTimeout(resolve, 5000));
                    const statusRes = await axios.get(`${apiUrl}/download/status/${jobId}`, {
                        headers: sharedHeaders,
                    });
                    if (statusRes.data?.status === "success") {
                        downloadUrls = statusRes.data?.download_urls ?? [];
                        break;
                    }
                    // status === "processing" → keep polling
                }
            }

            // Step 3: Trigger download for each URL via hidden iframe
            setStatus({ type: "success" });
            const downloadViaIframe = (url: string): Promise<void> => {
                return new Promise((resolve) => {
                    const iframe = document.createElement("iframe");
                    iframe.style.display = "none";
                    iframe.src = url;
                    iframe.onload = () => {
                        document.body.removeChild(iframe);
                        resolve();
                    };
                    document.body.appendChild(iframe);
                });
            };

            for (const url of downloadUrls) {
                await downloadViaIframe(url);
            }
        } catch (err: any) {
            setError("Something went wrong. Please try again.");
            console.error("[DownloadCSV] error:", err);
            setStatus({ type: "idle" });
            setWarnWhen(false);
            return;
        }

        // Reset after 2s
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setStatus({ type: "idle" });
        setWarnWhen(false);
    };

    return { status, error, download };
};
